/**
 * 分摊预览面板组件
 * 实时显示每个选中航段的分摊金额计算结果。
 *
 * 口径（与提交共用 computeAllocation）：原币按分摊规则拆到各段，各段美元 = 该段原币 × 该段航班日汇率。
 */

import React, { useMemo } from "react";
import { List, Typography, Space, Divider, Empty, Alert, Button, message } from "antd";
import { DollarCircleOutlined, AimOutlined, FileTextOutlined, ExclamationCircleOutlined, SettingOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { computeAllocation } from "../utils/computeAllocation";

const { Text, Title } = Typography;

/**
 * 分摊预览面板组件
 */
const AllocationPreview = ({ costData, selectedLegs, rateValidation }) => {
    const navigate = useNavigate();

    // 格式化日期为 YYYY-MM-DD 格式
    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
            return dateStr;
        }
        try {
            const date = new Date(dateStr);
            return date.toISOString().split('T')[0];
        } catch (e) {
            return dateStr;
        }
    };

    // 跳转到汇率管理页面
    const handleGoToRateManagement = (leg) => {
        try {
            navigate('/master-data/exchange-rates', {
                state: {
                    highlightCurrency: costData?.currency,
                    missingDateRanges: [formatDate(leg.flightDate)],
                    fromPage: 'cost-allocation'
                }
            });
        } catch (error) {
            message.error('跳转失败: ' + error.message);
        }
    };

    const isBhMode = costData?.allocationMethod === 'BY_BLOCK_HOURS';
    const isUsd = costData?.currency === 'USD';

    // 计算每个航段的分摊金额（与提交共用同一套口径）
    const { results: allocationResults, totalUsd } = useMemo(
        () => computeAllocation({ costData, selectedLegs, rateValidation }),
        [costData, selectedLegs, rateValidation]
    );

    // 选中航段总 BH（BH×单价模式展示用）
    const totalBlockHours = selectedLegs.reduce((sum, leg) => sum + (parseFloat(leg.blockHours) || 0), 0);

    // 检查费用数据是否完整：平均分摊看金额，BH×单价看单价
    const isCostDataComplete = costData && costData.supplier && costData.costType && (
        isBhMode ? (costData.unitPrice > 0) : (costData.amount > 0)
    );

    // 空状态处理
    if (!isCostDataComplete || selectedLegs.length === 0) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '200px',
                padding: '40px 20px'
            }}>
                <FileTextOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
                <Text type="secondary" style={{ fontSize: 14, textAlign: 'center' }}>
                    请先填写费用信息并选择航段
                </Text>
            </div>
        );
    }

    return (
        <div>
            {/* 分摊明细列表 */}
            <List
                size="small"
                bordered
                dataSource={allocationResults}
                renderItem={(item) => {
                    const missingRate = item.rate == null;

                    return (
                        <List.Item style={{ padding: '8px 12px' }}>
                            <div style={{ width: '100%' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Space>
                                        <AimOutlined style={{ color: '#1890ff' }} />
                                        <Text strong>{item.route}</Text>
                                        <Text type="secondary">({item.flightNumber})</Text>
                                    </Space>
                                    <Text strong style={{ color: missingRate ? '#faad14' : '#52c41a', fontSize: '14px' }}>
                                        {missingRate ? '—' : `$${item.allocatedUsd.toFixed(2)}`}
                                    </Text>
                                </div>
                                <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>
                                    <Text type="secondary">
                                        {isBhMode
                                            ? `${item.allocatedOriginal.toFixed(2)} ${costData.currency} (${(item.ratio * 100).toFixed(1)}%)`
                                            : `原币 ${item.allocatedOriginal.toFixed(2)} ${costData.currency} (${(item.ratio * 100).toFixed(1)}%)`}
                                    </Text>
                                </div>

                                {/* 汇率有效状态显示 */}
                                {!isUsd && !missingRate && (
                                    <div style={{ marginTop: 6 }}>
                                        <Text type="secondary" style={{ fontSize: 11 }}>
                                            汇率: {item.rate.toFixed(6)} ({costData?.currency}/USD, {formatDate(item.flightDate)})
                                        </Text>
                                    </div>
                                )}

                                {/* 汇率缺失提示 */}
                                {!isUsd && missingRate && (
                                    <div style={{ marginTop: 8 }}>
                                        <Alert
                                            message={
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <Space size="small">
                                                        <ExclamationCircleOutlined />
                                                        <Text style={{ fontSize: 12 }}>
                                                            缺少 {formatDate(item.flightDate)} 的有效 {costData?.currency}/USD 汇率
                                                        </Text>
                                                    </Space>
                                                    <Button
                                                        size="small"
                                                        type="link"
                                                        icon={<SettingOutlined />}
                                                        onClick={() => handleGoToRateManagement(item)}
                                                        style={{ padding: '0 4px', height: 'auto' }}
                                                    >
                                                        设置汇率
                                                    </Button>
                                                </div>
                                            }
                                            type="error"
                                            showIcon={false}
                                            style={{
                                                padding: '4px 8px',
                                                fontSize: 12,
                                                lineHeight: 1.2,
                                                border: '1px solid #ffccc7',
                                                backgroundColor: '#fff2f0'
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </List.Item>
                    );
                }}
                style={{ maxHeight: '300px', overflowY: 'auto' }}
            />

            <Divider style={{ margin: '16px 0' }} />

            {/* 分摊汇总 */}
            <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '6px' }}>
                <Space direction="vertical" size="small">
                    {/* BH×单价：展示总BH与单价 */}
                    {isBhMode && (
                        <div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                总 BH: {totalBlockHours.toFixed(2)} h × 单价 {costData.unitPrice?.toFixed(2)} {costData.currency}/h
                            </Text>
                        </div>
                    )}
                    {/* 平均分摊：显示原币金额 */}
                    {!isBhMode && (
                        <div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                原币金额: {costData.amount?.toFixed(2)} {costData.currency}
                            </Text>
                        </div>
                    )}
                    <div>
                        <DollarCircleOutlined style={{ color: '#1890ff', marginRight: 8 }} />
                        <Text>分摊合计 (USD):</Text>
                    </div>
                    <Title level={4} style={{ margin: 0, color: totalUsd == null ? '#faad14' : '#1890ff' }}>
                        {totalUsd == null ? '汇率不完整' : `$${totalUsd.toFixed(2)}`}
                    </Title>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        共分摊至 {selectedLegs.length} 个航段
                    </Text>
                </Space>
            </div>

            {/* 分摊方法说明 */}
            <div style={{ marginTop: 12, padding: '8px', backgroundColor: '#f6f6f6', borderRadius: '4px' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    <strong>分摊方法:</strong> {isBhMode
                        ? '各航段费用 = 该航段 BH × 单价，美元按该段航班日汇率换算'
                        : '将原币金额平均分配到所有选中航段，美元按各段航班日汇率换算'}
                </Text>
            </div>
        </div>
    );
};


export default AllocationPreview;
